import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText } from 'lucide-react';
import { Pagination } from 'antd';

interface Contract {
  contract_info: {
    contract_code: string;
    file_name: string;
  } | null;
  contract_file_url: string | null;
  created_at: string;
}

interface DocumentsTabProps {
  docs?: Contract[];
  currentPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
}

// Mock data for testing
const mockContracts: Contract[] = [
  {
    contract_info: {
      contract_code: 'HD-2024-001234',
      file_name: 'Hop_dong_dau_tu_goi_A.pdf',
    },
    contract_file_url: 'https://example.com/contract1.pdf',
    created_at: '2024-01-15',
  },
  {
    contract_info: {
      contract_code: 'HD-2024-001235',
      file_name: 'Hop_dong_dau_tu_goi_B.pdf',
    },
    contract_file_url: 'https://example.com/contract2.pdf',
    created_at: '2024-02-20',
  },
  {
    contract_info: {
      contract_code: 'HD-2024-001236',
      file_name: 'Phu_luc_hop_dong.pdf',
    },
    contract_file_url: null,
    created_at: '2024-03-10',
  },
];

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  docs,
  currentPage = 1,
  totalItems = 0,
  onPageChange
}) => {
  const { t } = useTranslation();

  // Use mock data if no docs provided
  const contracts: Contract[] = docs || mockContracts;

  const handleDownload = (contract: Contract) => {
    if (contract.contract_file_url) {
      window.open(contract.contract_file_url, '_blank');
    }
  };

  return (
    <div className="space-y-4">
      {/* Title */}
      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
        {t('profile.documents.title', 'Danh sách hợp đồng')}
      </h3>

      {/* Contracts List */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[500px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('profile.documents.code', 'Mã HĐ')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('profile.documents.name', 'Tên')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('profile.documents.signDate', 'Ngày ký')}
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('profile.documents.download', 'Tải xuống')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {contracts.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('profile.documents.empty', 'Chưa có hợp đồng nào')}
                </td>
              </tr>
            ) : (
              contracts.map((contract, index) => (
                <tr key={contract.contract_info?.contract_code || index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap" title={contract.contract_info?.contract_code}>
                    {contract.contract_info?.contract_code
                      ? (contract.contract_info.contract_code.length > 20
                          ? contract.contract_info.contract_code.slice(0, 20) + '...'
                          : contract.contract_info.contract_code)
                      : 'N/A'}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      {contract.contract_info?.file_name || 'N/A'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {contract.created_at}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      disabled={!contract.contract_file_url}
                      onClick={() => handleDownload(contract)}
                      className="inline-flex items-center disabled:opacity-50 disabled:!cursor-not-allowed gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalItems > 0 && onPageChange && (
        <div className="flex justify-end pt-4">
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={10}
            onChange={onPageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;
